import React, { createContext, useCallback, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { APIProvider, APIProviderContext } from '@vis.gl/react-google-maps';
import { logError } from '../../../lib/datahub/errorLogging/captureError';

// ---------------------------------------------------------------------------
// Context
// ---------------------------------------------------------------------------

interface MapApiContextValue {
  /** Signal that the Maps JS SDK is needed. Safe to call multiple times. */
  requestMapApi: () => void;
  /** True once the API has been requested (SDK script is being loaded). */
  apiRequested: boolean;
  /** True once Google reported (gm_authFailure) that it rejected our key/referrer. */
  authFailed: boolean;
}

const MapApiContext = createContext<MapApiContextValue>({
  requestMapApi: () => {},
  apiRequested: false,
  authFailed: false,
});

/** Returns the map-api context so consumers can request the SDK on demand. */
export function useMapApiContext(): MapApiContextValue {
  return useContext(MapApiContext);
}

// ---------------------------------------------------------------------------
// Provider
// ---------------------------------------------------------------------------

interface MapOverlayProviderProps {
  children: ReactNode;
}

/**
 * A failure LOADING the Maps SDK — a bad or over-quota API key, a blocked
 * script, a dead network at the moment of injection.
 *
 * Until this existed, the loader failed silently: `apiRequested` stayed true,
 * every map rendered its skeleton forever, and nothing was reported anywhere.
 * Users saw "Loading map..." and we saw nothing at all.
 *
 * The error object here is OURS (the library constructs it on our origin), so
 * unlike anything thrown from inside Maps JS it carries a usable message and
 * reaches the store with a real stack, under its own fingerprint.
 */
function handleMapsLoadError(error: unknown): void {
  logError(error, {
    severity: 'error',
    componentName: 'MapOverlayProvider',
    additionalContext: { mapsFailure: 'sdk_load' },
  });
}

type AuthFailureWindow = Window & { gm_authFailure?: () => void };

/**
 * Google reports a rejected key/referrer ONLY by calling the global
 * `window.gm_authFailure`; the map itself just paints gray. Install it once
 * the SDK is requested, chain any prior handler, and restore it on cleanup.
 * The key is never logged.
 *
 * ASSUMES A SINGLE PROVIDER: `window.gm_authFailure` is one global slot, and
 * this provider is mounted once (App.tsx). A second concurrent provider would
 * chain over the first and unmount order could restore a stale handler.
 */
function useMapsAuthFailure(enabled: boolean): boolean {
  const [authFailed, setAuthFailed] = useState(false);
  useEffect(() => {
    if (!enabled) return undefined;
    const w = window as AuthFailureWindow;
    const prior = w.gm_authFailure;
    const handler = () => {
      setAuthFailed(true);
      logError(new Error('Google Maps rejected the API key or referrer (gm_authFailure)'), {
        severity: 'error',
        componentName: 'MapOverlayProvider',
        additionalContext: { surface: 'map_overlay', mapsFailure: 'auth_failure' },
      });
      prior?.();
    };
    w.gm_authFailure = handler;
    return () => {
      if (w.gm_authFailure === handler) {
        if (prior) w.gm_authFailure = prior;
        else delete w.gm_authFailure;
      }
    };
  }, [enabled]);
  return authFailed;
}

type ApiProviderValue = React.ContextType<typeof APIProviderContext>;

/** Rendered inside the real APIProvider; hands its context value up to the provider. */
function ApiContextBridge({ onValue }: { onValue: (value: ApiProviderValue) => void }) {
  const value = useContext(APIProviderContext);
  useLayoutEffect(() => { onValue(value); }, [value, onValue]);
  return null;
}

/**
 * MapOverlayProvider — lazy Google Maps SDK loader.
 *
 * The `APIProvider` (which injects ~200 KB of Maps JS) is NOT mounted on
 * initial render. It is mounted only after the first call to `requestMapApi()`,
 * which happens automatically when any map-aware component (useMapOverlay,
 * useGeocode) mounts on a page that actually uses maps.
 *
 * Pages that never show maps (login, public quotes, billing, etc.) will never
 * trigger this and will never load the Maps SDK.
 *
 * ISS-642: `children` must stay at ONE position in the tree. This provider sits
 * above every other provider and page (App.tsx); wrapping children in
 * `<APIProvider>` on the first request remounted the whole app (all state lost).
 * So the APIProvider mounts as a SIBLING and its context value is bridged onto
 * an `APIProviderContext.Provider` that always wraps the children.
 */
export function MapOverlayProvider({ children }: MapOverlayProviderProps) {
  const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
  const [apiRequested, setApiRequested] = useState(false);
  const [apiContext, setApiContext] = useState<ApiProviderValue>(null);

  // Use a ref to avoid re-creating the callback on every render.
  const apiRequestedRef = useRef(false);

  const requestMapApi = useCallback(() => {
    if (apiRequestedRef.current) return;
    apiRequestedRef.current = true;
    setApiRequested(true);
  }, []);

  const authFailed = useMapsAuthFailure(Boolean(apiKey) && apiRequested);

  // Built once: the real APIProvider makes a new context value on every render, so
  // re-rendering it each time the bridge forwards a value would never settle
  // ("Maximum update depth exceeded"). A stable element lets React skip it.
  const sdk = useMemo(
    () =>
      apiKey && apiRequested ? (
        <APIProvider apiKey={apiKey} onError={handleMapsLoadError}>
          <ApiContextBridge onValue={setApiContext} />
        </APIProvider>
      ) : null,
    [apiKey, apiRequested],
  );

  // Consumers mount <Map> once apiRequested is true, and <Map> throws without the
  // APIProvider context, so report it only once the bridged context is there
  // (with no key it never is, and maps stay in their loading state).
  const apiReady = apiContext !== null;
  const contextValue = useMemo(
    () => ({ requestMapApi, apiRequested: apiReady, authFailed }),
    [requestMapApi, apiReady, authFailed],
  );

  return (
    <MapApiContext.Provider value={contextValue}>
      {sdk}
      <APIProviderContext.Provider value={apiContext}>
        {children}
      </APIProviderContext.Provider>
    </MapApiContext.Provider>
  );
}
